export type LsBreadcrumbItem = {
  route: string
  label?: string
  labelKey?: string
}

type LsBreadcrumbPayload = LsBreadcrumbItem & {
  expiresAt: number
}

const STORAGE_KEY_PREFIX = 'wikibit:lsBreadcrumb:'
const STORAGE_MAX_AGE = 30 * 60 * 1000

/** 生成详情页来源面包屑的 localStorage key。 */
const getStorageKey = (detailKey: string) => `${STORAGE_KEY_PREFIX}${detailKey}`

/** 安全移除缓存项，兼容浏览器禁用 localStorage 的场景。 */
const removeStorageItem = (storageKey: string) => {
  try {
    localStorage.removeItem(storageKey)
  } catch {
    // localStorage 不可用时不需要额外处理。
  }
}

/** 统一清洗详情页唯一标识，避免空值写入缓存。 */
const normalizeDetailKey = (detailKey: string | number | null | undefined) => `${detailKey ?? ''}`.trim()

/** 只允许缓存站内路径，避免把外部地址作为跳转来源。 */
const normalizeRoute = (route: string) => {
  const routeValue = route.trim()

  if (!routeValue || !routeValue.startsWith('/') || routeValue.startsWith('//')) {
    return ''
  }

  return routeValue
}

/** 统一清洗翻译 key，缺失时返回空字符串。 */
const normalizeLabelKey = (labelKey: string | null | undefined) => String(labelKey ?? '').trim()

/** 根据已知路由形态回推来源翻译 key，兼容旧版只存 label 的缓存。 */
const inferLabelKeyFromRoute = (route: string) => {
  if (route.includes('/inspections/') && route.includes('/page/')) {
    return '82521'
  }

  if (route === '/survey' || route.endsWith('/survey')) {
    return '82521'
  }

  return ''
}

/** 判断缓存数据是否是可用的面包屑来源结构。 */
const isLsBreadcrumbPayload = (value: unknown): value is LsBreadcrumbPayload => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const payload = value as Partial<LsBreadcrumbPayload>

  const hasLabel = typeof payload.label === 'string' && payload.label.trim().length > 0
  const hasLabelKey = typeof payload.labelKey === 'string' && payload.labelKey.trim().length > 0

  return typeof payload.route === 'string' && typeof payload.expiresAt === 'number' && (hasLabel || hasLabelKey)
}

export const useLsBreadcrumb = () => {
  /**
   * 保存详情页来源面包屑。
   * @param detailKey 详情页唯一标识，通常使用 sid 或 urlPath。
   * @param breadcrumb 来源页站内路径，以及可选的展示文案或翻译 key。
   * @returns 无返回值。
   */
  const setBreadcrumb = (detailKey: string | number | null | undefined, breadcrumb: LsBreadcrumbItem) => {
    if (!import.meta.client) {
      return
    }

    const normalizedDetailKey = normalizeDetailKey(detailKey)
    const normalizedRoute = normalizeRoute(breadcrumb.route)
    const normalizedLabel = String(breadcrumb.label ?? '').trim()
    const normalizedLabelKey = normalizeLabelKey(breadcrumb.labelKey)

    if (!normalizedDetailKey || !normalizedRoute || (!normalizedLabel && !normalizedLabelKey)) {
      return
    }

    try {
      localStorage.setItem(
        getStorageKey(normalizedDetailKey),
        JSON.stringify({
          route: normalizedRoute,
          label: normalizedLabel || undefined,
          labelKey: normalizedLabelKey || undefined,
          expiresAt: Date.now() + STORAGE_MAX_AGE,
        }),
      )
    } catch {
      // localStorage 不可用时直接降级，详情页会使用默认面包屑。
    }
  }

  /**
   * 读取详情页来源面包屑，过期或异常时自动清理。
   * @param detailKey 详情页唯一标识，通常使用 sid 或 urlPath。
   * @returns 可用的来源面包屑，缺失时返回 null。
   */
  const getBreadcrumb = (detailKey: string | number | null | undefined): LsBreadcrumbItem | null => {
    if (!import.meta.client) {
      return null
    }

    const normalizedDetailKey = normalizeDetailKey(detailKey)
    if (!normalizedDetailKey) {
      return null
    }

    const storageKey = getStorageKey(normalizedDetailKey)

    try {
      const rawValue = localStorage.getItem(storageKey)
      if (!rawValue) {
        return null
      }

      const payload = JSON.parse(rawValue)
      if (!isLsBreadcrumbPayload(payload) || payload.expiresAt < Date.now()) {
        removeStorageItem(storageKey)
        return null
      }

      const normalizedRoute = normalizeRoute(payload.route)
      const normalizedLabel = String(payload.label ?? '').trim()
      const normalizedLabelKey = normalizeLabelKey(payload.labelKey) || inferLabelKeyFromRoute(normalizedRoute)
      if (!normalizedRoute || (!normalizedLabel && !normalizedLabelKey)) {
        removeStorageItem(storageKey)
        return null
      }

      return {
        route: normalizedRoute,
        label: normalizedLabel || undefined,
        labelKey: normalizedLabelKey || undefined,
      }
    } catch {
      removeStorageItem(storageKey)
      return null
    }
  }

  /**
   * 移除指定详情页的来源面包屑。
   * @param detailKey 详情页唯一标识，通常使用 sid 或 urlPath。
   * @returns 无返回值。
   */
  const removeBreadcrumb = (detailKey: string | number | null | undefined) => {
    if (!import.meta.client) {
      return
    }

    const normalizedDetailKey = normalizeDetailKey(detailKey)
    if (!normalizedDetailKey) {
      return
    }

    removeStorageItem(getStorageKey(normalizedDetailKey))
  }

  return {
    setBreadcrumb,
    getBreadcrumb,
    removeBreadcrumb,
  }
}
